/**
 * @file src/api/identity.js
 * @class IdentityEngine
 * @description 
 * Authoritative Engine for Identity & Access Management (IAM).
 * Orchestrates Service #3 (User Administration) and Service #5 (Profile Management).
 * 
 * Standards:
 * - 1.5: Object-Oriented Orchestration.
 * - 3.1: Contract-First (Zod Validation).
 * - 8.1: Structural Integrity.
 * - 1.2: Intention-Revealing Naming.
 */

import api from './client';
import MoksyLogger from '../utils/logger';
import { guest as GuestAPI } from './guest.js';

/**
 * Validate a payload the SERVER sent us — never fatally.
 *
 * ⚠️ `Schema.parse(response.data.data)` throws a ZodError when the service adds,
 * renames or nulls a field. That is OUR contract drifting, not anything the
 * person on the page did, and it reached them as the raw issues array
 * (`[{"origin":"string","code":"too_big",...}]`) where their profile should
 * have been — the whole screen replaced by machine-readable JSON.
 *
 * A request payload SHOULD be strict: the customer can fix a field they typed.
 * A response payload must not be, because there is nothing they can do about
 * it and the page can usually still render. So: keep the drift in the log
 * where an engineer will see it, and hand the page what the server actually
 * sent. `getPublicProfilesByIds` already did exactly this per-row; this is the
 * same rule everywhere else.
 */
const shaped = (schema, value, label) => {
  const result = schema.safeParse(value);
  if (result.success) return result.data;
  MoksyLogger.warn('API_CONTRACT_DRIFT', {
    contract: label,
    issues: (result.error?.issues || []).slice(0, 5).map((i) => ({
      field: (i.path || []).join('.'), code: i.code
    }))
  });
  return value;
};

import { 
  UserSchema, 
  UserListSchema, 
  ProfileUpdateSchema,
  AboutUpdateSchema,
  BillingUpdateSchema,
  WorkStatusPatchSchema,
  SettingsPatchSchema,
  EducationCreateSchema,
  EmploymentCreateSchema,
  SkillCreateSchema,
  CertificationCreateSchema,
  InviteSendSchema,
  SecurityQuestionsUpdateSchema,
  OrgProfileSchema,
  OrgCatalogEntrySchema,
  OrgSettingsPatchSchema,
  OrgEarningsPatchSchema,
  OrgPublicProfileSchema,
  UserPublicProfileSchema,
  authValidator
} from '../schemas/identity.schema';

// RF-021: claimGuestSession below writes/removes several of these keys
// inline; these keep the rest of that function from repeating the same
// try/catch six more times.
const safeSetItem = (key, value) => { try { localStorage.setItem(key, value); } catch { /* storage blocked */ } };
const safeRemoveItem = (key) => { try { localStorage.removeItem(key); } catch { /* storage blocked */ } };

class IdentityEngine {
  static GUEST_TOKEN_KEY = 'guestToken';
  static GUEST_PROJECT_KEY = 'guest_active_project_id';
  
  // =========================================================
  // 👤 SECTION: PROFILE MANAGEMENT (Service #5)
  // =========================================================

  /**
   * 🔍 FETCH PROFILE
   * Logic: Retrieves the authenticated identity's current profile.
   * @returns {Promise<Object>} Validated User Object.
   */
  static async fetchProfile() {
    const response = await api.get('/profile');
    
    // 🛡️ Standard 8.1: Structural Integrity Gate
    return shaped(UserSchema, response.data.data, 'UserSchema');
  }

  /**
   * 📝 PATCH PROFILE
   * Logic: Modifies the current user's session attributes.
   * @param {Object} payload - Attributes to update.
   */
  static async patchProfile(payload) {
    const validatedPayload = ProfileUpdateSchema.parse(payload);
    const response = await api.put('/profile', validatedPayload);
    
    return shaped(UserSchema, response.data.data, 'UserSchema');
  }

  static async me() {
    const response = await api.get('/profile/me');
    return response.data?.data || response.data;
  }

  static async updateAbout(payload) {
    const validated = AboutUpdateSchema.parse(payload);
    const response = await api.patch('/profile/about', validated);
    return response.data?.data || response.data;
  }

  static async uploadProfilePicture(file) {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('PROFILE.PICTURE_READ_FAILED'));
      reader.readAsDataURL(file);
    });
    const response = await api.post('/profile/picture', {
      fileName: file?.name || 'avatar.png',
      dataUrl
    });
    return response.data?.data || response.data;
  }

  static async removeProfilePicture() {
    const response = await api.delete('/profile/picture');
    return response.data?.data || response.data;
  }

  static async getBilling() {
    const response = await api.get('/profile/billing');
    return response.data?.data || response.data;
  }

  static async updateBilling(payload) {
    const validated = BillingUpdateSchema.parse(payload);
    const response = await api.put('/profile/billing', validated);
    return response.data?.data || response.data;
  }

  static async getWorkStatus() {
    const response = await api.get('/profile/work-status');
    return response.data?.data || response.data;
  }

  static async patchWorkStatus(payload) {
    const validated = WorkStatusPatchSchema.parse(payload);
    const response = await api.patch('/profile/work-status', validated);
    return response.data?.data || response.data;
  }

  static async getSettings() {
    const response = await api.get('/settings');
    return response.data?.data || response.data;
  }

  static async patchSettings(payload) {
    const validated = SettingsPatchSchema.parse(payload);
    const response = await api.patch('/settings', validated);
    return response.data?.data || response.data;
  }

  static async listEducation() {
    const response = await api.get('/profile/education');
    return response.data?.data || [];
  }

  static async addEducation(payload) {
    const validated = EducationCreateSchema.parse(payload);
    const response = await api.post('/profile/education', validated);
    return response.data?.data || response.data;
  }

  static async updateEducation(id, payload) {
    const validated = EducationCreateSchema.partial().parse(payload);
    const response = await api.put(`/profile/education/${id}`, validated);
    return response.data?.data || response.data;
  }

  static async deleteEducation(id) {
    const response = await api.delete(`/profile/education/${id}`);
    return response.data?.data || response.data;
  }

  static async listEmployment() {
    const response = await api.get('/profile/employment');
    return response.data?.data || [];
  }

  static async addEmployment(payload) {
    const validated = EmploymentCreateSchema.parse(payload);
    const response = await api.post('/profile/employment', validated);
    return response.data?.data || response.data;
  }

  static async updateEmployment(id, payload) {
    const validated = EmploymentCreateSchema.partial().parse(payload);
    const response = await api.put(`/profile/employment/${id}`, validated);
    return response.data?.data || response.data;
  }

  static async deleteEmployment(id) {
    const response = await api.delete(`/profile/employment/${id}`);
    return response.data?.data || response.data;
  }

  static async listProfileProjects() {
    const response = await api.get('/profile/projects');
    return response.data?.data || [];
  }

  static async exportMyData() {
    const response = await api.get('/profile/data-export', {
      responseType: 'blob'
    });
    return {
      blob: response.data,
      fileName: response.headers?.['content-disposition']?.match(/filename=\"?([^"]+)\"?/)?.[1] || 'moksy-user-data-export.json'
    };
  }

  static async requestDataErasure(payload = { confirmation: 'ERASE_MY_DATA' }) {
    const response = await api.post('/profile/erasure-request', payload);
    return response.data?.data || response.data;
  }

  static async listSkills() {
    const response = await api.get('/profile/skills');
    return response.data?.data || [];
  }

  static async addSkill(payload) {
    const validated = SkillCreateSchema.parse(payload);
    const response = await api.post('/profile/skills', validated);
    return response.data?.data || response.data;
  }

  static async updateSkill(id, payload) {
    const validated = SkillCreateSchema.partial().parse(payload);
    const response = await api.put(`/profile/skills/${id}`, validated);
    return response.data?.data || response.data;
  }

  static async deleteSkill(id) {
    const response = await api.delete(`/profile/skills/${id}`);
    return response.data?.data || response.data;
  }

  static async listCertifications() {
    const response = await api.get('/profile/certifications');
    return response.data?.data || [];
  }

  static async addCertification(payload) {
    const validated = CertificationCreateSchema.parse(payload);
    const response = await api.post('/profile/certifications', validated);
    return response.data?.data || response.data;
  }

  static async updateCertification(id, payload) {
    const validated = CertificationCreateSchema.partial().parse(payload);
    const response = await api.put(`/profile/certifications/${id}`, validated);
    return response.data?.data || response.data;
  }

  // ── 🧬 People master schema — stored blocks ──────────────────────────
  // Validated in the users service from the same declaration the forms are
  // drawn from, so these send what the form holds and read back what was
  // stored — no client-side copy of the rules to drift.
  static async getPerson() {
    const response = await api.get('/profile/person');
    return response.data?.data || response.data;
  }

  static async patchPersonSection(section, payload) {
    const response = await api.patch(`/profile/person/${encodeURIComponent(section)}`, payload);
    return response.data?.data || response.data;
  }

  static async patchAffiliation(orgId, payload) {
    const response = await api.patch(`/profile/affiliations/${encodeURIComponent(orgId)}`, payload);
    return response.data?.data || response.data;
  }

  static async adminGetPerson(userId) {
    const response = await api.get(`/users/${encodeURIComponent(userId)}/person`);
    return response.data?.data || response.data;
  }

  static async adminPatchPersonSection(userId, section, payload) {
    const response = await api.patch(`/users/${encodeURIComponent(userId)}/person/${encodeURIComponent(section)}`, payload);
    return response.data?.data || response.data;
  }

  static async adminListPeople(params = {}) {
    const response = await api.get('/admin/people', { params });
    return response.data?.data || response.data;
  }

  static async adminCreatePerson(payload) {
    const response = await api.post('/admin/people', payload);
    return response.data?.data || response.data;
  }

  static async deleteCertification(id) {
    const response = await api.delete(`/profile/certifications/${id}`);
    return response.data?.data || response.data;
  }

  // The gateway route behind this used to be a stub returning a hardcoded [].
  // It proxies for real now, and answers with { endorsements, total } rather
  // than a bare array — unwrapped here so callers keep the array they expect.
  static async listEndorsements() {
    const response = await api.get('/profile/endorsements');
    const data = response.data?.data || {};
    return Array.isArray(data) ? data : (data.endorsements || []);
  }

  // Both directions: what you have asked for, and what you have been asked.
  static async listEndorsementRequests() {
    const response = await api.get('/profile/endorsements/requests');
    return response.data?.data || { sent: [], received: [] };
  }

  static async respondToEndorsementRequest(requestId, decision, text = '') {
    const response = await api.post(`/profile/endorsements/requests/${requestId}/respond`, { decision, text });
    return response.data?.data || response.data;
  }

  static async requestEndorsement(payload) {
    const response = await api.post('/profile/endorsements/request', payload);
    return response.data?.data || response.data;
  }

  // =========================================================
  // 🔐 SECTION: AUTHENTICATION GATES (Service #2)
  // =========================================================

  /**
   * 🔑 AUTHENTICATE
   * Logic: Exchanges credentials for a machine-state session.
   */
  static async authenticate(credentials) {
    const payload = authValidator.login(credentials);
    const response = await api.post('/login', payload);
    return response.data.data;
  }

  static async completeMfaLogin(payload = {}) {
    const response = await api.post('/auth/mfa/complete', payload);
    return response.data?.data || response.data;
  }

  /**
   * 🆔 PROVISION
   * Logic: Initializes a new identity record in the platform vault.
   */
  static async provision(data) {
    const payload = authValidator.registration(data);
    const response = await api.post('/register', payload);
    return response.data;
  }

  static async requestEmailVerification(payload = {}) {
    const response = await api.post('/auth/verify-email/request', payload);
    return response.data?.data || response.data;
  }

  static async confirmEmailVerification(payload = {}) {
    const response = await api.post('/auth/verify-email/confirm', payload);
    return response.data?.data || response.data;
  }

  static async requestPasswordReset(payload = {}) {
    const response = await api.post('/auth/password-reset/request', payload);
    return response.data?.data || response.data;
  }

  static async verifyPasswordResetToken(payload = {}) {
    const response = await api.post('/auth/password-reset/verify', payload);
    return response.data?.data || response.data;
  }

  static async confirmPasswordReset(payload = {}) {
    const response = await api.post('/auth/password-reset/confirm', payload);
    return response.data?.data || response.data;
  }

  /**
   * 🔄 CLAIM GUEST SESSION
   * Logic: Transfers guest-owned records to the authenticated account.
   */
  static async claimGuestSession() {
    // RF-021: a throw here is wrapped into a rejected promise (this is an
    // async function), and the one caller already does `.catch(() => {})` —
    // so today this never reaches a render. Guarded anyway: a second caller
    // that forgets the `.catch()`, or an `await` added here later, would
    // otherwise resurrect exactly the failure mode useAuth.js's lazy
    // localStorage read had.
    let guestToken = null;
    try { guestToken = localStorage.getItem(IdentityEngine.GUEST_TOKEN_KEY); } catch { /* storage blocked */ }
    if (!guestToken) return null;
    // Not while the person is working AS the guest. This runs on every session
    // hydration, and a browser that still holds a member cookie hydrates one on
    // a guest page too — so a reload mid-interview converted the guest, deleted
    // the token and the project pointer, and the page re-minted a stranger who
    // got 403 on the interview they were in the middle of. The claim belongs to
    // an explicit sign-in or sign-up (the post-login intent, "Save my plan"),
    // where the person can see their project move; never to a background tick
    // on the guest route it is about to break.
    //
    // The home page counts as a guest surface too: a silent token refresh
    // (an httpOnly refresh cookie from an earlier sign-in) re-hydrates the
    // session while the visitor is typing an idea as a guest, and the claim
    // that followed deleted the pointer to the project they were about to open.
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path === '/' || path.startsWith('/guest/')) return null;
    } catch (_) { /* no window — not a browser */ }
    // Nor while ANOTHER tab on this browser is in the middle of the guest
    // session — localStorage is shared, so the token this would convert is the
    // one that tab is answering questions with. The guest page beats a
    // heartbeat while open; a fresh beat means "not now".
    if (GuestAPI.inUse()) return null;
    const now = Date.now();
    const attemptKey = 'guest_convert_attempt_meta';
    let metaRaw = null;
    try { metaRaw = localStorage.getItem(attemptKey); } catch { /* storage blocked */ }
    if (metaRaw) {
      try {
        const meta = JSON.parse(metaRaw);
        if (meta?.token === guestToken && Number(meta?.nextRetryAt || 0) > now) return null;
      } catch {
        // noop
      }
    }

    try {
      safeSetItem(attemptKey, JSON.stringify({ token: guestToken, nextRetryAt: now + 60_000 }));
      const response = await api.post('/guest/convert', { guestToken });
      // Kept under the recovery key, not dropped: it still names the session
      // that built the work, which is what "Recover my previous session" needs
      // if a guest page is open somewhere and now answers 403.
      safeSetItem('guestToken_previous', guestToken);
      safeRemoveItem(IdentityEngine.GUEST_TOKEN_KEY);
      safeRemoveItem(IdentityEngine.GUEST_PROJECT_KEY);
      safeRemoveItem(attemptKey);
      return response.data?.data || response.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400) {
        // 400 means the server READ the token and rejected it — already
        // converted, malformed, or spent. Nothing is lost by dropping it, and
        // keeping it would retry forever.
        //
        // 401 deliberately does NOT land here any more. It is the same
        // transient signal a restart or a key rotation produces, and answering
        // it by deleting both the guest token and the pointer to the guest's
        // project destroys the only two things that can find their work. That
        // is how a finished interview became a bare 404 on a report the screen
        // had just said was building — the third copy of a reflex that had
        // already cost the same visitor their session twice.
        //
        // The token is kept under the recovery key rather than deleted, for
        // the same reason: it names the owner of whatever was built.
        safeSetItem('guestToken_previous', guestToken);
        safeRemoveItem(IdentityEngine.GUEST_TOKEN_KEY);
        safeRemoveItem(IdentityEngine.GUEST_PROJECT_KEY);
        safeRemoveItem(attemptKey);
      } else if (status === 401) {
        // Back off and let the next attempt try the SAME token. A conversion
        // that cannot be authorised right now is not a conversion that can
        // never happen.
        safeSetItem(attemptKey, JSON.stringify({ token: guestToken, nextRetryAt: now + 60_000 }));
      } else if (status === 503) {
        safeSetItem(attemptKey, JSON.stringify({ token: guestToken, nextRetryAt: now + 5 * 60_000 }));
      }
      throw error;
    }
  }

  // =========================================================
  // 🛠️ SECTION: USER ADMINISTRATION (Service #3)
  // =========================================================

  /**
   * 📋 LIST REGISTRY
   * Logic: Retrieves the authoritative collection of all platform identities.
   * Supports server-side pagination and search via query params.
   *
   * @param {object} params - { page, limit, search, role, status }
   */
  static async listRegistry(params = {}) {
    const response = await api.get('/admin/users', { params });

    // 🛡️ Standard 8.2: Collection Integrity Gate
    return shaped(UserListSchema, response.data.data, 'UserListSchema');
  }

  static async adminGetUser(id) {
    const response = await api.get(`/admin/users/${id}`);
    return shaped(UserSchema, response.data.data, 'UserSchema');
  }

  /**
   * 🔧 MODIFY IDENTITY
   * Logic: Updates a specific identity record via administrative privilege.
   */
  static async modifyIdentity(id, data) {
    const response = await api.put(`/admin/users/${id}`, data);
    return shaped(UserSchema, response.data.data, 'UserSchema');
  }

  /**
   * ⛔ PURGE IDENTITY
   * Logic: Permanently removes an identity record from the registry.
   */
  static async purgeIdentity(id) {
    const response = await api.delete(`/admin/users/${id}`);
    
    // Logic: Return raw data for confirmation
    return response.data.data;
  }

  static async changePassword(payload) {
    const response = await api.patch('/auth/change-password', payload);
    return response.data?.data || response.data;
  }

  static async getSecurityQuestions() {
    const response = await api.get('/auth/security-questions');
    return response.data?.data || [];
  }

  static async verifySecurityQuestions(payload) {
    const response = await api.post('/auth/security-questions/verify', payload);
    return response.data?.data || response.data;
  }

  static async updateSecurityQuestions(payload) {
    const validated = SecurityQuestionsUpdateSchema.parse(payload);
    const response = await api.put('/auth/security-questions', validated);
    return response.data?.data || response.data;
  }

  static async getMfaStatus() {
    const response = await api.get('/auth/mfa/status');
    return response.data?.data || response.data;
  }

  static async setupTotpMfa() {
    const response = await api.post('/auth/mfa/totp/setup', {});
    return response.data?.data || response.data;
  }

  static async verifyTotpMfa(payload = {}) {
    const response = await api.post('/auth/mfa/totp/verify', payload);
    return response.data?.data || response.data;
  }

  static async getMfaRecoveryCodes() {
    const response = await api.get('/auth/mfa/recovery-codes');
    return response.data?.data || response.data;
  }

  static async disableMfa() {
    const response = await api.post('/auth/mfa/disable', {});
    return response.data?.data || response.data;
  }

  static async sendInvite(payload) {
    const validated = InviteSendSchema.parse(payload);
    const response = await api.post('/invites/send', validated);
    return response.data?.data || response.data;
  }

  static async listSentInvites(params = {}) {
    const response = await api.get('/invites/sent', { params });
    return response.data?.data || [];
  }

  static async listReceivedInvites(params = {}) {
    const response = await api.get('/invites/received', { params });
    return response.data?.data || [];
  }

  static async cancelInvite(id) {
    const response = await api.post(`/invites/${id}/cancel`);
    return response.data?.data || response.data;
  }

  static async acceptInvite(id) {
    const response = await api.post(`/invites/${id}/accept`);
    return response.data?.data || response.data;
  }

  static async declineInvite(id) {
    const response = await api.post(`/invites/${id}/decline`);
    return response.data?.data || response.data;
  }

  static async setRegistrationEnabled(enabled) {
    const response = await api.patch('/users/config/registration', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async setUsersReadOnly(enabled) {
    const response = await api.patch('/users/config/read-only', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async setProfileUpdatesEnabled(enabled) {
    const response = await api.patch('/users/config/profile-updates', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async setSecurityQuestionsRequired(enabled) {
    const response = await api.patch('/users/config/security-questions-required', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async setEmailVerificationDeadlineHours(hours) {
    const response = await api.patch('/users/config/email-verification-deadline-hours', { hours: Number(hours) });
    return response.data?.data || response.data;
  }

  static async setMaxFailedLoginAttempts(attempts) {
    const response = await api.patch('/users/config/max-failed-login-attempts', { attempts: Number(attempts) });
    return response.data?.data || response.data;
  }

  static async setLockoutDurationMinutes(minutes) {
    const response = await api.patch('/users/config/lockout-duration-minutes', { minutes: Number(minutes) });
    return response.data?.data || response.data;
  }

  static async setLockoutRequiresManualUnlock(enabled) {
    const response = await api.patch('/users/config/lockout-requires-manual-unlock', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async getAdminStats() {
    const response = await api.get('/users/admin/stats');
    return response.data?.data || response.data;
  }

  static async adminPatchUserActive(id, active, reason = '') {
    // The reason travels with the act. Suspending revokes every live session,
    // and the record carried who and when and never why.
    const response = await api.patch(`/users/${id}/active`, { active: !!active, reason });
    return response.data?.data || response.data;
  }

  /**
   * RF-073: the only sanction reachable from any admin screen used to be
   * `adminPatchUserActive`, which suspends the whole account — a reviewer
   * who is also a paying customer loses both to stop one. This route
   * (`PATCH /admin/reviewers/:userId/application`) freezes only the
   * reviewer's accreditation grants, which `evaluateClaim` reads fresh on
   * every claim attempt; a real, working, already-wired gate with no caller
   * anywhere in the frontend until now.
   */
  static async adminSetReviewerApplicationStatus(id, status, reason = '') {
    const response = await api.patch(`/admin/reviewers/${id}/application`, { status, reason });
    return response.data?.data || response.data;
  }

  static async adminUnlockUser(id) {
    const response = await api.post(`/users/${id}/unlock`, {});
    return response.data?.data || response.data;
  }

  static async adminSetUserMessage(id, message) {
    const response = await api.patch(`/users/${id}/admin-message`, { message: String(message || '') });
    return response.data?.data || response.data;
  }

  static async acknowledgeAdminMessage() {
    const response = await api.post('/users/me/admin-message/ack', {});
    return response.data?.data || response.data;
  }

  // 🏔️ LEVEL admin client methods. Mirror the gateway endpoints:
  //   GET    /users/admin/level/config         → admin+
  //   PATCH  /users/admin/level/config         → super_admin
  //   GET    /users/admin/level/distribution   → admin+
  //   PATCH  /users/:id/level                  → super_admin
  //   DELETE /users/:id/level                  → super_admin
  //   POST   /users/:id/level/signal           → admin+
  static async getLevelConfig() {
    const response = await api.get('/users/admin/level/config');
    return response.data?.data || response.data;
  }

  static async updateLevelConfig(payload) {
    const response = await api.patch('/users/admin/level/config', payload || {});
    return response.data?.data || response.data;
  }

  static async getLevelDistribution() {
    const response = await api.get('/users/admin/level/distribution');
    return response.data?.data || response.data;
  }

  static async adminSetUserLevel(id, level) {
    const response = await api.patch(`/users/${id}/level`, { level: String(level || '') });
    return response.data?.data || response.data;
  }

  static async adminClearUserLevel(id) {
    const response = await api.delete(`/users/${id}/level`);
    return response.data?.data || response.data;
  }

  // 🎯 CUSTOMER LEVEL admin client methods.
  static async getCustomerLevelConfig() {
    const response = await api.get('/users/admin/customer-level/config');
    return response.data?.data || response.data;
  }

  static async updateCustomerLevelConfig(payload) {
    const response = await api.patch('/users/admin/customer-level/config', payload || {});
    return response.data?.data || response.data;
  }

  static async getCustomerLevelDistribution() {
    const response = await api.get('/users/admin/customer-level/distribution');
    return response.data?.data || response.data;
  }

  static async adminSetUserCustomerLevel(id, level) {
    const response = await api.patch(`/users/${id}/customer-level`, { level: String(level || '') });
    return response.data?.data || response.data;
  }

  static async adminClearUserCustomerLevel(id) {
    const response = await api.delete(`/users/${id}/customer-level`);
    return response.data?.data || response.data;
  }

  static async usersHealthSync() {
    const response = await api.get('/users/health/sync');
    return response.data?.data || response.data;
  }

  // The users service used to return a bare array here — the WHOLE collection,
  // with no limit — and now returns a paginated envelope
  // ({ items, page, limit, total, pages }). Both shapes are accepted so this
  // does not become a deploy-ordering problem between two services, and the
  // array is still what callers get. Use `adminListUsersPaged` when the
  // pagination metadata is wanted.
  static async adminListUsers(params = {}) {
    const payload = await this.adminListUsersPaged(params);
    return payload.items;
  }

  static async adminListUsersPaged(params = {}) {
    const response = await api.get('/users', { params });
    const payload = response.data?.data;
    if (Array.isArray(payload)) {
      return { items: payload, page: 1, limit: payload.length, total: payload.length, pages: 1 };
    }
    return {
      items: payload?.items || [],
      page: payload?.page ?? 1,
      limit: payload?.limit ?? 25,
      total: payload?.total ?? 0,
      pages: payload?.pages ?? 1
    };
  }

  static async adminSearchUsers(q) {
    const response = await api.get('/users/search', { params: { q } });
    return response.data?.data || [];
  }

  static async adminCreateUser(payload) {
    const response = await api.post('/users/admin-create', payload);
    return response.data?.data || response.data;
  }

  static async adminPatchUserStatus(id, status) {
    const response = await api.patch(`/users/${id}/status`, { status });
    return response.data?.data || response.data;
  }

  static async adminPatchUserVerify(id, isVerified = true) {
    const response = await api.patch(`/users/${id}/verify`, { isVerified });
    return response.data?.data || response.data;
  }

  // DELETE carries no body reliably across proxies, so the reason rides as a
  // query parameter; the controller reads either.
  static async adminSoftDeleteUser(id, reason = '') {
    const response = await api.delete(`/users/${id}`, { params: { reason } });
    return response.data?.data || response.data;
  }

  static async adminHardDeleteUser(id, reason = '') {
    const response = await api.delete(`/users/${id}/permanent`, { params: { reason } });
    return response.data?.data || response.data;
  }

  static async adminPatchProfileAbout(id, payload) {
    const response = await api.patch(`/users/${id}/profile/about`, payload);
    return response.data?.data || response.data;
  }

  static async adminDeleteProfilePicture(id) {
    const response = await api.delete(`/users/${id}/profile/picture`);
    return response.data?.data || response.data;
  }

  static async adminPutEducation(id, education) {
    const response = await api.put(`/users/${id}/profile/professional/education`, { education });
    return response.data?.data || response.data;
  }

  static async adminPutEmployment(id, employment) {
    const response = await api.put(`/users/${id}/profile/professional/employment`, { employment });
    return response.data?.data || response.data;
  }

  static async adminDeleteSkill(id, skillId) {
    const response = await api.delete(`/users/${id}/profile/skills/${skillId}`);
    return response.data?.data || response.data;
  }

  static async adminDeleteEndorsement(id, endorsementId) {
    const response = await api.delete(`/users/${id}/profile/endorsements/${endorsementId}`);
    return response.data?.data || response.data;
  }

  static async adminPatchWorkSituation(id, acceptingRequests) {
    const response = await api.patch(`/users/${id}/profile/work-situation`, { acceptingRequests: !!acceptingRequests });
    return response.data?.data || response.data;
  }

  static async adminGetUserSettings(id) {
    const response = await api.get(`/users/${id}/settings`);
    return response.data?.data || response.data;
  }

  static async adminPatchUserSettings(id, payload) {
    const response = await api.patch(`/users/${id}/settings`, payload);
    return response.data?.data || response.data;
  }

  static async adminGetSecurityQuestions(id) {
    const response = await api.get(`/users/${id}/security-questions`);
    return response.data?.data || [];
  }

  static async adminResetSecurityQuestions(id) {
    const response = await api.post(`/users/${id}/security-questions/reset`, {});
    return response.data?.data || response.data;
  }

  static async adminGetAuditTrail(id) {
    const response = await api.get(`/users/${id}/audit-trail`);
    return response.data?.data || [];
  }

  static async adminSeedDummy(count = 10) {
    const response = await api.post('/users/seed/dummy', { count });
    return response.data?.data || response.data;
  }

  static async adminSeedProfessionalSuite() {
    const response = await api.post('/users/seed/professional-suite', {});
    return response.data?.data || response.data;
  }

  static async adminSeedCleanup() {
    const response = await api.delete('/users/seed/cleanup');
    return response.data?.data || response.data;
  }

  static async adminSimulateResetFlow(id) {
    const response = await api.post(`/users/${id}/simulate/reset-flow`, {});
    return response.data?.data || response.data;
  }

  static async adminSimulateOnboarding(id) {
    const response = await api.post(`/users/${id}/simulate/onboarding`, {});
    return response.data?.data || response.data;
  }

  static async adminSyncAuthProvider() {
    const response = await api.post('/users/sync/auth-provider', {});
    return response.data?.data || response.data;
  }

  static async adminSyncPermissions(userId = null) {
    const response = await api.post('/users/sync/permissions', userId ? { userId } : {});
    return response.data?.data || response.data;
  }

  static async adminExportBulk(format = 'json') {
    const response = await api.get('/users/export/bulk', { params: { format } });
    return response.data?.data || response.data;
  }

  static async adminImportBulk(users = []) {
    const response = await api.post('/users/import/bulk', { users });
    return response.data?.data || response.data;
  }

  static async adminGetMetadata(id) {
    const response = await api.get(`/users/${id}/metadata`);
    return response.data?.data || {};
  }

  static async adminPatchMetadata(id, metadata) {
    const response = await api.patch(`/users/${id}/metadata`, metadata);
    return response.data?.data || response.data;
  }

  static async startExternalLogin(provider) {
    const response = await api.get(`/auth/external/${provider}/login`);
    return response.data?.data || response.data;
  }

  static async completeExternalRegistration(payload) {
    const response = await api.post('/auth/external/register-complete', payload);
    return response.data?.data || response.data;
  }

  static async listMyExternalProviders() {
    const response = await api.get('/users/me/external-providers');
    return response.data?.data || [];
  }

  static async unlinkExternalProvider(provider) {
    const response = await api.delete(`/users/me/external-providers/${provider}`);
    return response.data?.data || response.data;
  }

  static async syncExternalProvider(provider, payload = {}) {
    const response = await api.post(`/users/me/external-providers/${provider}/sync`, payload);
    return response.data?.data || response.data;
  }

  static async adminGetExternalIdentities(userId) {
    const response = await api.get(`/admin/users/${userId}/external-identities`);
    return response.data?.data || [];
  }

  static async adminDeleteExternalIdentity(userId, provider) {
    const response = await api.delete(`/admin/users/${userId}/external-identities/${provider}`);
    return response.data?.data || response.data;
  }

  static async adminRegistrationSourceStats() {
    const response = await api.get('/admin/stats/registration-source');
    return response.data?.data || {};
  }

  static async setAuthProviderEnabled(provider, enabled) {
    const response = await api.patch(`/sys/config/auth/${provider}/enable`, { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  static async setAuthProviderAllowRegistration(provider, allowRegistration) {
    const response = await api.patch(`/sys/config/auth/${provider}/allow-registration`, { allowRegistration: !!allowRegistration });
    return response.data?.data || response.data;
  }

  static async updateAuthProviderCredentials(provider, clientId, clientSecret) {
    const response = await api.put(`/sys/config/auth/${provider}/credentials`, { clientId, clientSecret });
    return response.data?.data || response.data;
  }

  static async authProvidersHealth() {
    const response = await api.get('/sys/config/auth/providers/health');
    return response.data?.data || response.data;
  }

  static async setEnforceSocialEmailMatch(enabled) {
    const response = await api.patch('/sys/config/auth/enforce-social-email-match', { enabled: !!enabled });
    return response.data?.data || response.data;
  }

  // Backward-compat utility for older hooks
  static async getUsers() {
    return this.listRegistry();
  }

  // =========================================================
  // 🏢 SECTION: ORGANISATIONS (G5.1 / G5.2 / G5.3)
  // =========================================================

  /**
   * Create a new organisation. Authenticated user becomes the owner.
   */
  static async createOrg(data = {}) {
    const response = await api.post('/orgs', data);
    return response.data?.data || response.data;
  }

  /**
   * List all organisations the authenticated user belongs to.
   */
  static async listMyOrgs() {
    const response = await api.get('/orgs/mine');
    return response.data?.data || response.data;
  }

  /**
   * Fetch a single organisation by id.
   */
  static async getOrg(orgId) {
    const response = await api.get(`/orgs/${orgId}`);
    return response.data?.data || response.data;
  }

  /**
   * Update org metadata. Caller must be owner or admin.
   */
  static async updateOrg(orgId, data = {}) {
    const response = await api.patch(`/orgs/${orgId}`, data);
    return response.data?.data || response.data;
  }

  /**
   * Soft-delete an org. Only the owner may delete.
   */
  static async deleteOrg(orgId) {
    const response = await api.delete(`/orgs/${orgId}`);
    return response.data?.data || response.data;
  }

  /**
   * List members of an org.
   */
  static async listOrgMembers(orgId) {
    const response = await api.get(`/orgs/${orgId}/members`);
    return response.data?.data || response.data;
  }

  /**
   * Remove a member from an org.
   */
  static async removeOrgMember(orgId, userId) {
    const response = await api.delete(`/orgs/${orgId}/members/${userId}`);
    return response.data?.data || response.data;
  }

  /**
   * Open (or reuse) a chat thread with another member of the org.
   * The users service verifies shared membership and opens the thread
   * in the communications service as system. Returns { threadId } (which
   * may be null if communications is temporarily unavailable).
   * @param {string} orgId
   * @param {string} userId  the member to message
   */
  static async messageOrgMember(orgId, userId) {
    const response = await api.post(`/orgs/${orgId}/members/${userId}/message`, {});
    return response.data?.data || response.data;
  }

  /**
   * Send an org invitation to an email address.
   * @param {string} orgId
   * @param {string} email
   * @param {'admin'|'member'} role
   */
  static async inviteToOrg(orgId, email, role = 'member') {
    const response = await api.post(`/orgs/${orgId}/invitations`, { email, role });
    return response.data?.data || response.data;
  }

  /**
   * List pending invitations for an org.
   */
  static async listOrgInvitations(orgId) {
    const response = await api.get(`/orgs/${orgId}/invitations`);
    return response.data?.data || response.data;
  }

  /**
   * Accept an org invitation by token.
   * Authenticated — the logged-in user is joined to the org.
   */
  static async acceptOrgInvitation(token) {
    const response = await api.post('/orgs/invitations/accept', { token });
    return response.data?.data || response.data;
  }

  /**
   * Revoke a pending org invitation.
   */
  static async revokeOrgInvitation(orgId, token) {
    const response = await api.delete(`/orgs/${orgId}/invitations/${token}`);
    return response.data?.data || response.data;
  }

  /**
   * List projects scoped to an organisation.
   */
  static async listOrgProjects(orgId, query = {}) {
    const params = new URLSearchParams(query).toString();
    const response = await api.get(`/orgs/${orgId}/projects${params ? `?${params}` : ''}`);
    return response.data?.data || response.data;
  }

  /**
   * Create a project scoped to an organisation.
   */
  static async createOrgProject(orgId, data = {}) {
    const response = await api.post(`/orgs/${orgId}/projects`, data);
    return response.data?.data || response.data;
  }

  static async getOrgProfile(orgId) {
    const response = await api.get(`/orgs/${orgId}/profile`);
    return shaped(OrgProfileSchema, response.data?.data || response.data, 'OrgProfileSchema');
  }

  static async patchOrgProfile(orgId, payload = {}) {
    const response = await api.patch(`/orgs/${orgId}/profile`, payload);
    return response.data?.data || response.data;
  }

  static async getOrgSettings(orgId) {
    const response = await api.get(`/orgs/${orgId}/settings`);
    return response.data?.data || response.data;
  }

  static async getOrgAiTokenSummary(orgId) {
    const response = await api.get(`/orgs/${orgId}/ai-token-summary`);
    return response.data?.data || response.data;
  }

  static async patchOrgSettings(orgId, payload = {}) {
    const validated = OrgSettingsPatchSchema.parse(payload);
    const response = await api.patch(`/orgs/${orgId}/settings`, validated);
    return response.data?.data || response.data;
  }

  static async listOrgUsers(orgId) {
    const response = await api.get(`/orgs/${orgId}/users`);
    return response.data?.data || [];
  }

  static async listOrgCatalog(orgId, catalogKey) {
    const response = await api.get(`/orgs/${orgId}/${catalogKey}`);
    return response.data?.data || [];
  }

  static async addOrgCatalogEntry(orgId, catalogKey, payload = {}) {
    const validated = OrgCatalogEntrySchema.parse(payload);
    const response = await api.post(`/orgs/${orgId}/${catalogKey}`, validated);
    return response.data?.data || response.data;
  }

  static async updateOrgCatalogEntry(orgId, catalogKey, entryId, payload = {}) {
    const validated = OrgCatalogEntrySchema.partial().parse(payload);
    const response = await api.put(`/orgs/${orgId}/${catalogKey}/${entryId}`, validated);
    return response.data?.data || response.data;
  }

  static async deleteOrgCatalogEntry(orgId, catalogKey, entryId) {
    const response = await api.delete(`/orgs/${orgId}/${catalogKey}/${entryId}`);
    return response.data?.data || response.data;
  }

  static async getOrgEarnings(orgId) {
    const response = await api.get(`/orgs/${orgId}/earnings`);
    return response.data?.data || {};
  }

  static async patchOrgEarnings(orgId, payload = {}) {
    const validated = OrgEarningsPatchSchema.parse(payload);
    const response = await api.patch(`/orgs/${orgId}/earnings`, validated);
    return response.data?.data || response.data;
  }

  static async getPublicUserProfile(userId) {
    const response = await api.get(`/public/users/${userId}/profile`);
    return shaped(UserPublicProfileSchema, response.data?.data || response.data, 'UserPublicProfileSchema');
  }

  /**
   * 📦 BATCH: resolve many public user profiles in a single request.
   * Returns an array of the same public-profile shape as the single
   * endpoint. Unknown/deleted ids are simply omitted by the server, so
   * the array may be shorter than `ids`. Individual entries are parsed
   * defensively — a malformed row is dropped rather than failing the batch.
   */
  static async getPublicUserProfiles(ids = []) {
    const clean = Array.from(new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    ));
    if (!clean.length) return [];
    const response = await api.post('/public/users/profiles', { ids: clean });
    const rows = response.data?.data || response.data || [];
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => {
        try { return UserPublicProfileSchema.parse(row); }
        catch { return null; }
      })
      .filter(Boolean);
  }

  static async getPublicOrgProfile(orgId) {
    const response = await api.get(`/public/orgs/${orgId}/profile`);
    return shaped(OrgPublicProfileSchema, response.data?.data || response.data, 'OrgPublicProfileSchema');
  }

  static async getPublicOrgProfileBySlug(slug) {
    const response = await api.get(`/public/orgs/slug/${slug}/profile`);
    return shaped(OrgPublicProfileSchema, response.data?.data || response.data, 'OrgPublicProfileSchema');
  }
}

/**
 * 🛡️ AUTHORITATIVE ALIAS
 * Standard 1.3: Provides a unified interface for the UI layer.
 */
export { IdentityEngine as identity };
